import { useCallback, useMemo, useState } from "react";

import { InfoOutlinedIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { areInstructionsEmpty } from "utils/common";

import Box from "components/Box";
import Dialog, { DialogContent, DialogTitle } from "components/Dialog";
import Paper from "components/Paper";
import SanitizeHtml from "components/SanitizeHtml";
import Stack from "components/Stack";
import Text from "components/Text";
import Tooltip from "components/Tooltip";

export interface IInstructionsDialogButton {
  internalInstructions: string | null | undefined;
  externalInstructions: string | null | undefined;
  sx?: any;
}
const InstructionsDialogButton = ({
  internalInstructions,
  externalInstructions,
  sx,
}: IInstructionsDialogButton) => {
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const showInternalInstructions = useMemo(
    () => !areInstructionsEmpty({ instructions: internalInstructions }),
    [internalInstructions]
  );

  const showExternalInstructions = useMemo(
    () => !areInstructionsEmpty({ instructions: externalInstructions }),
    [externalInstructions]
  );

  return showInternalInstructions || showExternalInstructions ? (
    <>
      {showInternalInstructions && (
        <Tooltip title="Reviewer Instructions" placement="top">
          <Box
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              ...sx,
            }}
            data-testid="instructions-icon"
          >
            <InfoOutlinedIcon
              sx={{
                width: "16px",
                height: "16px",
                color: colors.pink100,
              }}
              onClick={handleOpen}
            />
          </Box>
        </Tooltip>
      )}

      {showExternalInstructions && (
        <Tooltip
          title={`${showInternalInstructions ? "Public" : ""} Instructions`}
          placement="top"
        >
          <Box
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              ...sx,
            }}
            data-testid="instructions-icon"
          >
            <InfoOutlinedIcon
              sx={{
                width: "16px",
                height: "16px",
                color: colors.blue100,
              }}
              onClick={handleOpen}
            />
          </Box>
        </Tooltip>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ padding: theme.spacing(2, 2, 3, 2) }}>
          <Paper sx={{ backgroundColor: colors.blue10, borderRadius: "4px" }}>
            <Text variant="h2">Instructions</Text>
          </Paper>
        </DialogTitle>
        <DialogContent sx={{ padding: theme.spacing(1.5, 4, 3, 4) }}>
          {showInternalInstructions && (
            <Stack direction="column" spacing={1}>
              <Stack alignItems="center" direction="row" spacing={1}>
                <InfoOutlinedIcon
                  sx={{
                    width: "16px",
                    height: "16px",
                    color: colors.pink100,
                  }}
                />
                <Text variant="label" color={colors.pink100}>
                  reviewer instructions
                </Text>
              </Stack>
              <SanitizeHtml html={internalInstructions || ""} />
            </Stack>
          )}

          {showExternalInstructions && (
            <Stack
              direction="column"
              spacing={1}
              marginTop={showInternalInstructions ? 1.5 : 0}
            >
              <Stack alignItems="center" direction="row" spacing={1}>
                <InfoOutlinedIcon
                  sx={{
                    width: "16px",
                    height: "16px",
                    color: colors.blue100,
                  }}
                />
                <Text variant="label" color={colors.blue100}>
                  {showInternalInstructions ? "public " : ""}
                  instructions
                </Text>
              </Stack>
              <SanitizeHtml html={externalInstructions || ""} />
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </>
  ) : null;
};

export default InstructionsDialogButton;
